import React, { useMemo, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

/**
 * Mood Meter Quadrant Detection
 * RED: High Energy + Negative (anger, stress, anxiety)
 * YELLOW: High Energy + Positive (excitement, joy, enthusiasm)
 * BLUE: Low Energy + Negative (sadness, fatigue, depression)
 * GREEN: Low Energy + Positive (calm, peaceful, content)
 */
const detectQuadrant = (adjective) => {
  if (!adjective) return 'neutral';
  const adj = adjective.toLowerCase();
  
  // RED: High Energy + Negative
  const redWords = [
    'enraged', 'panicked', 'stressed', 'jittery', 'shocked', 'livid', 'furious',
    'frustrated', 'tense', 'stunned', 'fuming', 'frightened', 'angry', 'nervous',
    'restless', 'anxious', 'apprehensive', 'worried', 'irritated', 'annoyed',
    'repulsed', 'troubled', 'concerned', 'uneasy', 'peeved'
  ];
  
  // YELLOW: High Energy + Positive
  const yellowWords = [
    'surprised', 'upbeat', 'festive', 'exhilarated', 'ecstatic', 'hyper',
    'cheerful', 'motivated', 'inspired', 'elated', 'energized', 'lively',
    'enthusiastic', 'optimistic', 'excited', 'pleased', 'happy', 'focused',
    'proud', 'thrilled', 'pleasant', 'joyful', 'hopeful', 'playful', 'blissful'
  ];
  
  // BLUE: Low Energy + Negative
  const blueWords = [
    'disgusted', 'glum', 'disappointed', 'down', 'apathetic', 'pessimistic',
    'morose', 'discouraged', 'sad', 'bored', 'alienated', 'miserable', 'lonely',
    'disheartened', 'tired', 'despondent', 'depressed', 'sullen', 'exhausted',
    'fatigued', 'despair', 'hopeless', 'desolate', 'spent', 'drained'
  ];
  
  // GREEN: Low Energy + Positive
  const greenWords = [
    'at ease', 'easygoing', 'content', 'loving', 'fulfilled', 'calm', 'secure',
    'satisfied', 'grateful', 'touched', 'relaxed', 'chill', 'restful', 'blessed',
    'balanced', 'mellow', 'thoughtful', 'peaceful', 'comfy', 'carefree', 'sleepy',
    'complacent', 'tranquil', 'cozy', 'serene'
  ];
  
  if (redWords.includes(adj)) return 'red';
  if (yellowWords.includes(adj)) return 'yellow';
  if (blueWords.includes(adj)) return 'blue';
  if (greenWords.includes(adj)) return 'green';
  
  // Fallback keyword matching
  if (/angry|stress|anx|worry|fear|panic|frustrat|tense|nervous|irritat/.test(adj)) return 'red';
  if (/happy|excit|joy|optim|hope|cheer|enthus|thrill|elat|inspir/.test(adj)) return 'yellow';
  if (/sad|depress|tired|exhaust|lone|disappoint|hopeless|drain|fatigue/.test(adj)) return 'blue';
  if (/calm|peace|relax|content|seren|tranquil|satisf|grateful|balance/.test(adj)) return 'green';
  
  return 'neutral';
};

/**
 * Time-of-day color temperature adjustment
 * Dawn/Dusk: Warmer (orange tint)
 * Day: Neutral/Bright
 * Night: Cooler (blue tint)
 */
const getTimeOfDayShift = () => {
  const hour = new Date().getHours();
  
  // Night (10pm - 5am): Cool blue shift
  if (hour >= 22 || hour < 5) {
    return { hueShift: 20, saturationMod: 0.9, brightnessMod: 0.85, period: 'night' };
  }
  // Dawn (5am - 7am): Warm golden hour
  if (hour >= 5 && hour < 7) {
    return { hueShift: -15, saturationMod: 1.1, brightnessMod: 1.0, period: 'dawn' };
  }
  // Morning (7am - 11am): Slightly warm
  if (hour >= 7 && hour < 11) {
    return { hueShift: -5, saturationMod: 1.05, brightnessMod: 1.05, period: 'morning' };
  }
  // Midday (11am - 3pm): Neutral, bright
  if (hour >= 11 && hour < 15) {
    return { hueShift: 0, saturationMod: 1.0, brightnessMod: 1.1, period: 'midday' };
  }
  // Afternoon (3pm - 6pm): Slightly warm
  if (hour >= 15 && hour < 18) {
    return { hueShift: -8, saturationMod: 1.05, brightnessMod: 1.0, period: 'afternoon' };
  }
  // Dusk/Golden hour (6pm - 8pm): Very warm
  if (hour >= 18 && hour < 20) {
    return { hueShift: -20, saturationMod: 1.15, brightnessMod: 0.95, period: 'dusk' };
  }
  // Evening (8pm - 10pm): Cooling down
  if (hour >= 20 && hour < 22) {
    return { hueShift: 10, saturationMod: 0.95, brightnessMod: 0.9, period: 'evening' };
  }
  
  return { hueShift: 0, saturationMod: 1.0, brightnessMod: 1.0, period: 'day' };
};

// Apply time-of-day shift to a hex color
const applyTimeShift = (hexColor, timeShift) => {
  if (!hexColor || hexColor.length < 7) return hexColor;
  
  // Parse hex to RGB
  const hex = hexColor.replace('#', '');
  let r = parseInt(hex.substring(0, 2), 16);
  let g = parseInt(hex.substring(2, 4), 16);
  let b = parseInt(hex.substring(4, 6), 16);
  
  // Convert to HSL
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h, s, l = (max + min) / 2;
  
  if (max === min) {
    h = s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
      case g: h = ((b - r) / d + 2) / 6; break;
      case b: h = ((r - g) / d + 4) / 6; break;
      default: h = 0;
    }
  }
  
  // Apply time-of-day adjustments
  h = ((h * 360 + timeShift.hueShift) % 360) / 360;
  if (h < 0) h += 1;
  s = Math.min(1, Math.max(0, s * timeShift.saturationMod));
  l = Math.min(0.85, Math.max(0.15, l * timeShift.brightnessMod));
  
  // Convert back to RGB
  let r2, g2, b2;
  if (s === 0) {
    r2 = g2 = b2 = l;
  } else {
    const hue2rgb = (p, q, t) => {
      if (t < 0) t += 1;
      if (t > 1) t -= 1;
      if (t < 1/6) return p + (q - p) * 6 * t;
      if (t < 1/2) return q;
      if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
      return p;
    };
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r2 = hue2rgb(p, q, h + 1/3);
    g2 = hue2rgb(p, q, h);
    b2 = hue2rgb(p, q, h - 1/3);
  }
  
  const toHex = (c) => Math.round(c * 255).toString(16).padStart(2, '0');
  return `#${toHex(r2)}${toHex(g2)}${toHex(b2)}`;
};

// Extended per-adjective animation variations (100+ unique animations)
const getAdjectiveAnimation = (adjective, intensity = 0.5) => {
  if (!adjective) return null;
  const adj = adjective.toLowerCase();
  
  // Intensity modifier for more dramatic animations at high intensity
  const im = 0.7 + intensity * 0.6; // 0.7 to 1.3 multiplier
  
  // Each adjective gets unique animation characteristics
  const adjectiveAnimations = {
    // HIGH ENERGY NEGATIVE (Red quadrant) - Chaotic, sharp movements
    'enraged': { 
      scale: [1, 1.25*im, 0.85, 1.22*im, 0.9, 1.18*im, 1], 
      rotate: [0, -10, 12, -8, 6, -4, 0], 
      filter: [`hue-rotate(0deg)`, `hue-rotate(-10deg)`, `hue-rotate(5deg)`, `hue-rotate(0deg)`],
      duration: 0.7 
    },
    'panicked': { 
      scale: [1, 1.35*im, 0.8, 1.25*im, 0.85, 1.15*im, 1], 
      y: [0, -15*im, 8, -12*im, 5, -6, 0], 
      x: [0, 5, -8, 6, -4, 2, 0],
      duration: 0.5 
    },
    'stressed': { 
      scale: [1, 1.12*im, 0.93, 1.1*im, 0.95, 1.08*im, 1], 
      rotate: [0, -3, 4, -2, 3, -1, 0],
      y: [0, -2, 3, -2, 1, 0, 0],
      duration: 1.1 
    },
    'jittery': { 
      scale: [1, 1.06*im, 0.97, 1.04*im, 0.98, 1.03*im, 1], 
      x: [0, 4, -5, 3, -4, 2, 0],
      y: [0, -2, 3, -1, 2, -1, 0],
      duration: 0.35 
    },
    'shocked': { 
      scale: [1, 1.5*im, 0.85, 1.15, 1], 
      opacity: [1, 0.7, 1, 0.9, 1],
      duration: 0.4 
    },
    'livid': { 
      scale: [1, 1.22*im, 0.88, 1.2*im, 0.9, 1.15*im, 1], 
      rotate: [0, -8, 10, -6, 8, -3, 0],
      duration: 0.9 
    },
    'furious': { 
      scale: [1, 1.28*im, 0.82, 1.22*im, 0.88, 1.18*im, 1], 
      rotate: [0, -12, 15, -10, 8, -5, 0],
      x: [0, 3, -4, 2, -3, 1, 0],
      duration: 0.8 
    },
    'frustrated': { 
      scale: [1, 1.1*im, 0.94, 1.08*im, 0.96, 1.05*im, 1], 
      rotate: [0, -4, 5, -3, 2, -1, 0],
      duration: 1.4 
    },
    'tense': { 
      scale: [1, 1.08*im, 0.95, 1.06*im, 0.97, 1.04*im, 1],
      y: [0, -1, 2, -1, 1, 0, 0],
      duration: 1.6 
    },
    'stunned': {
      scale: [1, 1.35*im, 0.9, 1.1, 1],
      opacity: [1, 0.6, 1, 0.85, 1],
      y: [0, -8, 4, -2, 0],
      duration: 0.6
    },
    'fuming': {
      scale: [1, 1.18*im, 0.9, 1.15*im, 0.92, 1.1*im, 1],
      rotate: [0, -6, 8, -5, 4, -2, 0],
      y: [0, -4, 2, -3, 1, 0, 0],
      duration: 1.0
    },
    'frightened': {
      scale: [1, 1.2*im, 0.88, 1.12*im, 0.92, 1.08*im, 1],
      y: [0, -10*im, 5, -6, 3, -2, 0],
      x: [0, 3, -4, 2, -2, 1, 0],
      duration: 0.9
    },
    'angry': {
      scale: [1, 1.15*im, 0.92, 1.12*im, 0.94, 1.08*im, 1],
      rotate: [0, -5, 6, -4, 3, -1, 0],
      duration: 1.1
    },
    'nervous': { 
      scale: [1, 1.08*im, 0.95, 1.06*im, 0.97, 1.04*im, 1], 
      x: [0, 3, -4, 2, -3, 1, 0],
      y: [0, -2, 1, -1, 1, 0, 0],
      duration: 1.0 
    },
    'restless': { 
      scale: [1, 1.08*im, 0.96, 1.06*im, 0.97, 1.04*im, 1], 
      x: [0, 5, -6, 4, -3, 2, 0], 
      y: [0, -3, 4, -2, 2, -1, 0], 
      duration: 0.9 
    },
    'anxious': { 
      scale: [1, 1.14*im, 0.92, 1.1*im, 0.94, 1.06*im, 1], 
      y: [0, -5*im, 3, -3, 2, -1, 0],
      x: [0, 2, -2, 1, -1, 0, 0],
      duration: 1.2 
    },
    'apprehensive': {
      scale: [1, 1.08*im, 0.95, 1.06*im, 0.97, 1.03*im, 1],
      y: [0, -3, 2, -2, 1, 0, 0],
      duration: 1.5
    },
    'worried': { 
      scale: [1, 1.12*im, 0.94, 1.08*im, 0.96, 1.05*im, 1], 
      rotate: [0, -2, 3, -2, 1, 0, 0],
      y: [0, -2, 2, -1, 1, 0, 0],
      duration: 1.5 
    },
    'irritated': {
      scale: [1, 1.08*im, 0.95, 1.06*im, 0.97, 1.04*im, 1],
      rotate: [0, -3, 4, -2, 2, -1, 0],
      duration: 1.3
    },
    'annoyed': {
      scale: [1, 1.06*im, 0.96, 1.04*im, 0.98, 1.02*im, 1],
      rotate: [0, -2, 2, -1, 1, 0, 0],
      duration: 1.6
    },
    'repulsed': {
      scale: [1, 0.92, 1.05*im, 0.95, 1.02, 0.98, 1],
      y: [0, 4, -2, 3, -1, 1, 0],
      duration: 1.4
    },
    'troubled': { 
      scale: [1, 1.1*im, 0.94, 1.06*im, 0.96, 1.04*im, 1], 
      y: [0, 3, -1, 2, -1, 1, 0],
      duration: 1.7 
    },
    'uneasy': { 
      scale: [1, 1.06*im, 0.96, 1.04*im, 0.98, 1.02*im, 1], 
      rotate: [0, -1, 2, -1, 1, 0, 0],
      y: [0, -1, 2, -1, 1, 0, 0],
      duration: 1.9 
    },
    'concerned': { 
      scale: [1, 1.08*im, 0.95, 1.05*im, 0.97, 1.03*im, 1],
      y: [0, -2, 1, -1, 1, 0, 0],
      duration: 2.0 
    },
    'peeved': {
      scale: [1, 1.05*im, 0.97, 1.03*im, 0.98, 1.02*im, 1],
      rotate: [0, -2, 2, -1, 1, 0, 0],
      duration: 1.8
    },
    
    // HIGH ENERGY POSITIVE (Yellow quadrant) - Bouncy, upward movements
    'ecstatic': { 
      scale: [1, 1.3*im, 1.08, 1.25*im, 1.1, 1.18*im, 1], 
      y: [0, -18*im, -8, -15*im, -5, -10, 0],
      rotate: [0, 3, -2, 2, -1, 1, 0],
      duration: 1.0 
    },
    'exhilarated': {
      scale: [1, 1.28*im, 1.1, 1.22*im, 1.12, 1.15*im, 1],
      y: [0, -16*im, -6, -12*im, -4, -8, 0],
      duration: 1.1
    },
    'excited': { 
      scale: [1, 1.22*im, 1.06, 1.18*im, 1.08, 1.12*im, 1], 
      y: [0, -12*im, -4, -10*im, -3, -6, 0],
      duration: 1.3 
    },
    'elated': { 
      scale: [1, 1.24*im, 1.1, 1.18*im, 1.12, 1.14*im, 1], 
      y: [0, -14*im, -5, -10*im, -4, -7, 0],
      rotate: [0, 2, -1, 1, 0, 0, 0],
      duration: 1.2 
    },
    'thrilled': { 
      scale: [1, 1.26*im, 1.08, 1.2*im, 1.1, 1.14*im, 1], 
      y: [0, -13*im, -5, -10*im, -3, -6, 0],
      duration: 1.2 
    },
    'hyper': {
      scale: [1, 1.2*im, 1.02, 1.18*im, 1.04, 1.12*im, 1],
      y: [0, -10*im, -2, -8*im, -1, -5, 0],
      x: [0, 3, -4, 2, -2, 1, 0],
      duration: 0.8
    },
    'festive': {
      scale: [1, 1.18*im, 1.08, 1.14*im, 1.1, 1.1*im, 1],
      y: [0, -8*im, -3, -6*im, -2, -4, 0],
      rotate: [0, 3, -2, 2, -1, 1, 0],
      duration: 1.4
    },
    'surprised': {
      scale: [1, 1.35*im, 1.05, 1.15, 1],
      y: [0, -12*im, -2, -5, 0],
      duration: 0.7
    },
    'upbeat': {
      scale: [1, 1.15*im, 1.05, 1.1*im, 1.06, 1.08*im, 1],
      y: [0, -7*im, -2, -5*im, -1, -3, 0],
      duration: 1.5
    },
    'joyful': { 
      scale: [1, 1.18*im, 1.06, 1.12*im, 1.08, 1.1*im, 1], 
      y: [0, -8*im, -3, -6*im, -2, -4, 0],
      duration: 1.6 
    },
    'happy': { 
      scale: [1, 1.14*im, 1.05, 1.1*im, 1.06, 1.08*im, 1], 
      y: [0, -6*im, -2, -5*im, -1, -3, 0],
      duration: 1.8 
    },
    'cheerful': { 
      scale: [1, 1.12*im, 1.04, 1.08*im, 1.05, 1.06*im, 1], 
      y: [0, -5*im, -1, -4*im, -1, -2, 0],
      duration: 2.0 
    },
    'motivated': { 
      scale: [1, 1.15*im, 1.05, 1.1*im, 1.06, 1.08*im, 1], 
      y: [0, -6*im, -2, -5*im, -1, -3, 0],
      rotate: [0, 1, -1, 1, 0, 0, 0],
      duration: 1.6 
    },
    'inspired': { 
      scale: [1, 1.18*im, 1.06, 1.12*im, 1.08, 1.1*im, 1], 
      y: [0, -8*im, -3, -6*im, -2, -4, 0], 
      rotate: [0, 3, -2, 2, -1, 1, 0],
      duration: 1.8 
    },
    'energized': { 
      scale: [1, 1.16*im, 1.08, 1.12*im, 1.09, 1.1*im, 1], 
      y: [0, -7*im, -3, -5*im, -2, -4, 0],
      duration: 1.4 
    },
    'lively': {
      scale: [1, 1.14*im, 1.05, 1.1*im, 1.06, 1.07*im, 1],
      y: [0, -6*im, -2, -4*im, -1, -3, 0],
      x: [0, 2, -2, 1, -1, 0, 0],
      duration: 1.5
    },
    'enthusiastic': {
      scale: [1, 1.16*im, 1.06, 1.12*im, 1.07, 1.09*im, 1],
      y: [0, -7*im, -2, -5*im, -1, -3, 0],
      duration: 1.5
    },
    'optimistic': { 
      scale: [1, 1.1*im, 1.03, 1.07*im, 1.04, 1.05*im, 1], 
      y: [0, -4*im, -1, -3*im, -1, -2, 0],
      duration: 2.2 
    },
    'hopeful': { 
      scale: [1, 1.12*im, 1.05, 1.08*im, 1.06, 1.06*im, 1], 
      y: [0, -5*im, -2, -4*im, -1, -2, 0],
      duration: 2.0 
    },
    'pleased': { 
      scale: [1, 1.1*im, 1.04, 1.06*im, 1.04, 1.05*im, 1],
      y: [0, -3*im, -1, -2*im, 0, -1, 0],
      duration: 2.2 
    },
    'focused': {
      scale: [1, 1.08*im, 1.02, 1.05*im, 1.03, 1.04*im, 1],
      duration: 2.5
    },
    'proud': { 
      scale: [1, 1.12*im, 1.06, 1.09*im, 1.07, 1.07*im, 1], 
      y: [0, -4*im, -1, -3*im, -1, -2, 0],
      duration: 2.0 
    },
    'pleasant': {
      scale: [1, 1.08*im, 1.03, 1.05*im, 1.04, 1.04*im, 1],
      y: [0, -2*im, -1, -2*im, 0, -1, 0],
      duration: 2.5
    },
    'playful': {
      scale: [1, 1.12*im, 1.02, 1.1*im, 1.04, 1.06*im, 1],
      y: [0, -5*im, -1, -4*im, -1, -2, 0],
      x: [0, 3, -3, 2, -1, 1, 0],
      rotate: [0, 2, -3, 2, -1, 0, 0],
      duration: 1.4
    },
    'blissful': {
      scale: [1, 1.1*im, 1.05, 1.08*im, 1.06, 1.06*im, 1],
      y: [0, -4*im, -2, -3*im, -1, -2, 0],
      duration: 2.2
    },
    
    // LOW ENERGY NEGATIVE (Blue quadrant) - Sinking, slow movements
    'depressed': { 
      scale: [1, 0.9, 0.85, 0.88, 0.92, 0.95, 1], 
      y: [0, 8, 14, 10, 6, 3, 0],
      opacity: [1, 0.85, 0.75, 0.8, 0.9, 0.95, 1],
      duration: 4.5 
    },
    'hopeless': { 
      scale: [1, 0.88, 0.82, 0.85, 0.9, 0.94, 1], 
      y: [0, 10, 18, 12, 7, 4, 0],
      opacity: [1, 0.8, 0.7, 0.75, 0.85, 0.92, 1],
      duration: 5.0 
    },
    'despair': { 
      scale: [1, 0.85, 0.78, 0.82, 0.88, 0.93, 1], 
      y: [0, 12, 22, 15, 9, 5, 0],
      opacity: [1, 0.75, 0.65, 0.7, 0.82, 0.9, 1],
      duration: 5.5 
    },
    'desolate': {
      scale: [1, 0.87, 0.8, 0.84, 0.89, 0.94, 1],
      y: [0, 10, 18, 13, 8, 4, 0],
      opacity: [1, 0.78, 0.68, 0.73, 0.84, 0.91, 1],
      duration: 5.2
    },
    'despondent': {
      scale: [1, 0.88, 0.82, 0.85, 0.9, 0.94, 1],
      y: [0, 9, 16, 11, 7, 4, 0],
      duration: 4.8
    },
    'miserable': { 
      scale: [1, 0.9, 0.85, 0.88, 0.92, 0.95, 1], 
      y: [0, 8, 13, 9, 6, 3, 0],
      duration: 4.2 
    },
    'sad': { 
      scale: [1, 0.94, 0.9, 0.92, 0.95, 0.97, 1], 
      y: [0, 5, 9, 6, 4, 2, 0],
      duration: 3.8 
    },
    'lonely': { 
      scale: [1, 0.92, 0.88, 0.9, 0.93, 0.96, 1], 
      y: [0, 6, 10, 7, 5, 3, 0],
      duration: 4.5 
    },
    'alienated': {
      scale: [1, 0.91, 0.86, 0.89, 0.93, 0.96, 1],
      y: [0, 7, 12, 8, 5, 3, 0],
      opacity: [1, 0.9, 0.82, 0.86, 0.92, 0.96, 1],
      duration: 4.4
    },
    'discouraged': { 
      scale: [1, 0.94, 0.9, 0.92, 0.95, 0.97, 1], 
      y: [0, 5, 8, 5, 3, 2, 0],
      duration: 3.8 
    },
    'disappointed': { 
      scale: [1, 0.95, 0.91, 0.93, 0.96, 0.98, 1], 
      y: [0, 4, 7, 5, 3, 1, 0],
      duration: 3.5 
    },
    'disheartened': {
      scale: [1, 0.94, 0.89, 0.91, 0.94, 0.97, 1],
      y: [0, 5, 9, 6, 4, 2, 0],
      duration: 3.9
    },
    'glum': { 
      scale: [1, 0.94, 0.9, 0.92, 0.95, 0.97, 1], 
      y: [0, 4, 7, 5, 3, 2, 0],
      duration: 4.0 
    },
    'down': {
      scale: [1, 0.95, 0.91, 0.93, 0.96, 0.98, 1],
      y: [0, 4, 6, 4, 3, 1, 0],
      duration: 3.6
    },
    'morose': {
      scale: [1, 0.92, 0.87, 0.9, 0.93, 0.96, 1],
      y: [0, 6, 10, 7, 5, 2, 0],
      duration: 4.2
    },
    'pessimistic': {
      scale: [1, 0.94, 0.9, 0.92, 0.95, 0.97, 1],
      y: [0, 4, 7, 5, 3, 1, 0],
      duration: 3.8
    },
    'sullen': {
      scale: [1, 0.93, 0.89, 0.91, 0.94, 0.97, 1],
      y: [0, 5, 8, 6, 4, 2, 0],
      duration: 4.0
    },
    'exhausted': { 
      scale: [1, 0.92, 0.86, 0.89, 0.93, 0.96, 1], 
      y: [0, 6, 11, 8, 5, 3, 0],
      duration: 4.8 
    },
    'fatigued': {
      scale: [1, 0.93, 0.88, 0.9, 0.94, 0.97, 1],
      y: [0, 5, 9, 7, 4, 2, 0],
      duration: 4.5
    },
    'tired': { 
      scale: [1, 0.95, 0.92, 0.94, 0.96, 0.98, 1], 
      y: [0, 4, 6, 4, 3, 1, 0],
      duration: 4.0 
    },
    'drained': { 
      scale: [1, 0.92, 0.87, 0.9, 0.94, 0.97, 1], 
      y: [0, 6, 10, 7, 5, 2, 0],
      duration: 4.5 
    },
    'spent': {
      scale: [1, 0.93, 0.88, 0.9, 0.94, 0.97, 1],
      y: [0, 5, 9, 7, 4, 2, 0],
      duration: 4.6
    },
    'bored': { 
      scale: [1, 0.98, 0.96, 0.97, 0.98, 0.99, 1],
      duration: 5.5 
    },
    'apathetic': { 
      scale: [1, 0.97, 0.95, 0.96, 0.98, 0.99, 1],
      opacity: [1, 0.95, 0.9, 0.92, 0.96, 0.98, 1],
      duration: 6.0 
    },
    'disgusted': {
      scale: [1, 0.92, 1.05, 0.95, 1.02, 0.98, 1],
      y: [0, 5, -3, 4, -1, 2, 0],
      duration: 2.5
    },
    
    // LOW ENERGY POSITIVE (Green quadrant) - Gentle, breathing movements
    'serene': { 
      scale: [1, 1.05, 1.08, 1.06, 1.04, 1.02, 1],
      opacity: [1, 1, 0.95, 1, 0.98, 1, 1],
      duration: 7.0 
    },
    'tranquil': { 
      scale: [1, 1.04, 1.07, 1.05, 1.03, 1.02, 1],
      duration: 7.5 
    },
    'peaceful': { 
      scale: [1, 1.05, 1.08, 1.06, 1.04, 1.02, 1],
      y: [0, -1, -2, -1, 0, 0, 0],
      duration: 6.0 
    },
    'calm': { 
      scale: [1, 1.04, 1.06, 1.05, 1.03, 1.02, 1],
      duration: 5.5 
    },
    'relaxed': { 
      scale: [1, 1.05, 1.07, 1.05, 1.04, 1.02, 1],
      y: [0, -1, -1, 0, 0, 0, 0],
      duration: 5.8 
    },
    'content': { 
      scale: [1, 1.04, 1.06, 1.04, 1.03, 1.02, 1],
      duration: 5.5 
    },
    'satisfied': { 
      scale: [1, 1.05, 1.07, 1.05, 1.03, 1.02, 1],
      y: [0, -1, -2, -1, 0, 0, 0],
      duration: 5.2 
    },
    'grateful': { 
      scale: [1, 1.06, 1.09, 1.06, 1.04, 1.02, 1],
      y: [0, -2, -3, -2, -1, 0, 0],
      duration: 5.0 
    },
    'fulfilled': {
      scale: [1, 1.06, 1.08, 1.06, 1.04, 1.02, 1],
      y: [0, -2, -3, -2, -1, 0, 0],
      duration: 5.2
    },
    'loving': {
      scale: [1, 1.06, 1.1, 1.07, 1.04, 1.02, 1],
      y: [0, -2, -4, -2, -1, 0, 0],
      duration: 4.5
    },
    'touched': {
      scale: [1, 1.05, 1.08, 1.06, 1.04, 1.02, 1],
      y: [0, -2, -3, -1, -1, 0, 0],
      duration: 4.8
    },
    'secure': {
      scale: [1, 1.03, 1.05, 1.04, 1.02, 1.01, 1],
      duration: 5.5
    },
    'at ease': {
      scale: [1, 1.04, 1.06, 1.04, 1.03, 1.02, 1],
      duration: 5.8
    },
    'easygoing': {
      scale: [1, 1.03, 1.05, 1.04, 1.02, 1.01, 1],
      x: [0, 1, 0, -1, 0, 0, 0],
      duration: 5.5
    },
    'balanced': { 
      scale: [1, 1.04, 1.06, 1.04, 1.03, 1.02, 1],
      duration: 6.0 
    },
    'mellow': { 
      scale: [1, 1.03, 1.05, 1.03, 1.02, 1.01, 1],
      duration: 6.5 
    },
    'thoughtful': {
      scale: [1, 1.04, 1.05, 1.04, 1.03, 1.02, 1],
      y: [0, -1, -1, 0, 0, 0, 0],
      duration: 5.5
    },
    'blessed': {
      scale: [1, 1.05, 1.08, 1.06, 1.04, 1.02, 1],
      y: [0, -2, -3, -2, -1, 0, 0],
      duration: 5.2
    },
    'restful': {
      scale: [1, 1.03, 1.05, 1.04, 1.02, 1.01, 1],
      duration: 6.2
    },
    'chill': {
      scale: [1, 1.02, 1.04, 1.03, 1.02, 1.01, 1],
      duration: 6.5
    },
    'cozy': { 
      scale: [1, 1.04, 1.06, 1.04, 1.03, 1.02, 1],
      y: [0, 1, 2, 1, 0, 0, 0],
      duration: 6.0 
    },
    'comfy': {
      scale: [1, 1.03, 1.05, 1.04, 1.02, 1.01, 1],
      y: [0, 1, 2, 1, 0, 0, 0],
      duration: 6.2
    },
    'carefree': {
      scale: [1, 1.04, 1.06, 1.05, 1.03, 1.02, 1],
      x: [0, 1, -1, 1, 0, 0, 0],
      duration: 5.5
    },
    'complacent': {
      scale: [1, 1.02, 1.03, 1.02, 1.01, 1.01, 1],
      duration: 7.0
    },
    'sleepy': { 
      scale: [1, 1.02, 1.04, 1.02, 1.01, 1.01, 1], 
      y: [0, 2, 4, 3, 2, 1, 0],
      opacity: [1, 0.95, 0.9, 0.92, 0.96, 0.98, 1],
      duration: 7.5 
    },
  };
  
  return adjectiveAnimations[adj] || null;
};

// Quadrant-based fallback animation configurations.
// `glow` gives each quadrant its own outer-glow character so different moods read
// as distinct: red = fast/sharp/flickering, yellow = wide/radiant, blue = dim/slow,
// green = gentle/steady.
const quadrantAnimations = {
  red: {
    orb: { scale: [1, 1.15, 0.95, 1.1, 1], rotate: [0, -3, 3, -2, 0] },
    transition: { duration: 1.5, repeat: Infinity, ease: "easeInOut" },
    rings: 4,
    particles: 12,
    particleSpeed: 0.5,
    glow: { scale: [1, 1.28, 1.02, 1.22, 1], opacity: [0.35, 0.62, 0.38, 0.55, 0.35], duration: 1.8, ease: "easeInOut", blur: 12, sizeScale: 1.0 },
  },
  yellow: {
    orb: { scale: [1, 1.12, 1.05, 1.1, 1], y: [0, -5, 0, -3, 0] },
    transition: { duration: 2, repeat: Infinity, ease: "easeOut" },
    rings: 3,
    particles: 10,
    particleSpeed: 0.7,
    glow: { scale: [1, 1.34, 1.16, 1.28, 1], opacity: [0.4, 0.66, 0.5, 0.6, 0.4], duration: 2.6, ease: "easeOut", blur: 16, sizeScale: 1.12 },
  },
  blue: {
    orb: { scale: [1, 0.97, 0.95, 0.98, 1], y: [0, 3, 5, 2, 0] },
    transition: { duration: 4, repeat: Infinity, ease: "easeInOut" },
    rings: 2,
    particles: 6,
    particleSpeed: 1.5,
    glow: { scale: [1, 1.07, 1.02, 1.05, 1], opacity: [0.2, 0.32, 0.24, 0.29, 0.2], duration: 6.5, ease: "easeInOut", blur: 24, sizeScale: 0.92 },
  },
  green: {
    orb: { scale: [1, 1.03, 1.05, 1.03, 1] },
    transition: { duration: 5, repeat: Infinity, ease: "easeInOut" },
    rings: 2,
    particles: 4,
    particleSpeed: 2,
    glow: { scale: [1, 1.12, 1.06, 1.1, 1], opacity: [0.3, 0.44, 0.34, 0.4, 0.3], duration: 5.5, ease: "easeInOut", blur: 22, sizeScale: 1.0 },
  },
  neutral: {
    orb: { scale: [1, 1.04, 1.02, 1.03, 1] },
    transition: { duration: 3.5, repeat: Infinity, ease: "easeInOut" },
    rings: 2,
    particles: 6,
    particleSpeed: 1.2,
    glow: { scale: [1, 1.14, 1.07, 1.11, 1], opacity: [0.28, 0.44, 0.35, 0.41, 0.28], duration: 4, ease: "easeInOut", blur: 18, sizeScale: 1.0 },
  },
};

// Per-quadrant particle motion so the floating dots move differently by mood:
//   red = erratic jittery bursts, yellow = rising/floating up, blue = slow sinking,
//   green = gentle orbit, neutral = soft outward drift.
const getParticleMotion = (quadrant, angleDeg, intensity, reachMult = 1) => {
  const a = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(a), sin = Math.sin(a);
  const r = 45;
  const reach = (75 + intensity * 30) * reachMult;
  switch (quadrant) {
    case 'red':
      return { anim: { x: [cos * r, cos * reach + 9, cos * r * 0.7 - 7, cos * reach * 0.85 + 5, cos * r], y: [sin * r, sin * reach - 7, sin * r * 0.7 + 8, sin * reach * 0.85 - 4, sin * r], opacity: [0, 0.7, 0.3, 0.6, 0], scale: [0.3, 0.9, 0.45, 0.8, 0.3] }, ease: 'easeOut' };
    case 'yellow':
      return { anim: { x: [cos * r, cos * r + 10, cos * reach * 0.7, cos * reach, cos * reach + 6], y: [sin * r + 18, sin * r, -reach * 0.55, -reach, -reach * 1.25], opacity: [0, 0.7, 0.6, 0.35, 0], scale: [0.4, 1.0, 0.85, 0.6, 0.3] }, ease: 'easeOut' };
    case 'blue':
      return { anim: { x: [cos * r, cos * r * 1.1, cos * r * 1.15, cos * r * 1.1, cos * r], y: [sin * r - 8, sin * r + 24 * reachMult, sin * r + 50 * reachMult, sin * r + 72 * reachMult, sin * r + 90 * reachMult], opacity: [0, 0.45, 0.4, 0.2, 0], scale: [0.5, 0.7, 0.6, 0.5, 0.4] }, ease: 'easeIn' };
    case 'green':
      return { anim: { x: [cos * r, Math.cos(a + 1.3) * r, Math.cos(a + 2.6) * r, Math.cos(a + 3.9) * r, Math.cos(a + 5.2) * r], y: [sin * r, Math.sin(a + 1.3) * r, Math.sin(a + 2.6) * r, Math.sin(a + 3.9) * r, Math.sin(a + 5.2) * r], opacity: [0, 0.5, 0.55, 0.5, 0], scale: [0.4, 0.7, 0.75, 0.7, 0.4] }, ease: 'easeInOut' };
    default:
      return { anim: { x: [cos * r, cos * reach * 0.85, cos * r], y: [sin * r, sin * reach * 0.85, sin * r], opacity: [0, 0.5, 0], scale: [0.4, 0.8, 0.4] }, ease: 'easeInOut' };
  }
};

export const MoodOrb = ({ color, intensity = 0.5, adjective }) => {
  const [timeShift, setTimeShift] = useState(getTimeOfDayShift());
  const quadrant = detectQuadrant(adjective);
  const adjectiveAnim = getAdjectiveAnimation(adjective, intensity);
  const baseConfig = quadrantAnimations[quadrant] || quadrantAnimations.neutral;
  
  // Update time shift every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeShift(getTimeOfDayShift());
    }, 60000); // Update every minute
    return () => clearInterval(interval);
  }, []);
  
  // Apply time-of-day color shift
  const adjustedColor = useMemo(() => {
    return applyTimeShift(color, timeShift);
  }, [color, timeShift]);
  
  // Use adjective-specific animation if available, otherwise fall back to quadrant
  const orbAnimation = useMemo(() => {
    if (adjectiveAnim) {
      return {
        scale: adjectiveAnim.scale,
        rotate: adjectiveAnim.rotate || [0, 0, 0, 0, 0, 0, 0],
        x: adjectiveAnim.x || [0, 0, 0, 0, 0, 0, 0],
        y: adjectiveAnim.y || [0, 0, 0, 0, 0, 0, 0],
        opacity: adjectiveAnim.opacity || [1, 1, 1, 1, 1, 1, 1],
      };
    }
    return baseConfig.orb;
  }, [adjectiveAnim, baseConfig.orb]);
  
  const orbTransition = useMemo(() => {
    if (adjectiveAnim) {
      return {
        duration: adjectiveAnim.duration,
        repeat: Infinity,
        ease: quadrant === 'red' ? 'easeInOut' : quadrant === 'yellow' ? 'easeOut' : 'easeInOut',
      };
    }
    return baseConfig.transition;
  }, [adjectiveAnim, baseConfig.transition, quadrant]);
  
  // Generate particles based on quadrant
  const particles = useMemo(() =>
    Array.from({ length: baseConfig.particles }, (_, i) => ({
      id: i,
      // Spread evenly, then jitter so they don't sit on a perfect ring
      angle: (i / baseConfig.particles) * 360 + (Math.random() - 0.5) * 55,
      delay: Math.random() * baseConfig.particleSpeed * 2.5,
      durMult: 0.7 + Math.random() * 0.8,    // 0.7–1.5x duration
      reachMult: 0.7 + Math.random() * 0.9,  // vary travel distance
    })), [baseConfig.particles, baseConfig.particleSpeed]
  );
  
  // Generate rings
  const rings = useMemo(() => 
    Array.from({ length: baseConfig.rings }, (_, i) => ({
      id: i,
      delay: i * 0.4,
      scale: 1.3 + (i + 1) * 0.3,
    })), [baseConfig.rings]
  );

  const glowSize = 50 + intensity * 50;

  return (
    <div 
      className={`mood-orb-container mood-quadrant-${quadrant}`} 
      data-testid="mood-orb" 
      data-quadrant={quadrant} 
      data-adjective={adjective}
      data-time-period={timeShift.period}
    >
      {/* Outer glow — character varies per mood quadrant (see quadrantAnimations.glow) */}
      <motion.div
        className="mood-orb-glow"
        style={{
          background: `radial-gradient(circle, ${adjustedColor}40 0%, ${adjustedColor}15 50%, transparent 70%)`,
          filter: `blur(${(baseConfig.glow?.blur ?? 15) + intensity * 15}px)`,
        }}
        animate={{
          scale: (baseConfig.glow?.scale ?? [1, 1.2, 1.1, 1.15, 1]).map(s => s * (baseConfig.glow?.sizeScale ?? 1)),
          opacity: baseConfig.glow?.opacity ?? [0.3, 0.5, 0.4, 0.45, 0.3],
        }}
        transition={{ duration: baseConfig.glow?.duration ?? (3 + (1 - intensity) * 2), repeat: Infinity, ease: baseConfig.glow?.ease ?? "easeInOut" }}
      />

      {/* Pulsing rings */}
      {rings.map((ring) => (
        <motion.div
          key={ring.id}
          className="mood-orb-ring"
          style={{
            borderColor: `${adjustedColor}30`,
          }}
          animate={{
            scale: [1, ring.scale],
            opacity: [0.4, 0],
          }}
          transition={{
            duration: 2.5 + ring.id * 0.5,
            repeat: Infinity,
            delay: ring.delay,
            ease: "easeOut",
          }}
        />
      ))}

      {/* Main orb with adjective-specific animation */}
      <motion.div 
        className="mood-orb"
        style={{ 
          background: `radial-gradient(circle at 35% 35%, ${adjustedColor}ff, ${adjustedColor}bb, ${adjustedColor}88)`,
          boxShadow: `
            0 0 ${glowSize}px ${adjustedColor}66,
            0 0 ${glowSize * 1.3}px ${adjustedColor}33,
            inset 0 0 ${25 + intensity * 15}px ${adjustedColor}44
          `,
        }}
        animate={orbAnimation}
        transition={orbTransition}
      />

      {/* Inner core highlight */}
      <motion.div 
        className="mood-orb-core"
        style={{ 
          background: `radial-gradient(circle, rgba(255,255,255,0.85), ${adjustedColor})`,
        }}
        animate={{ 
          scale: [1, 1.2, 1.1, 1.15, 1],
          opacity: [0.4, 0.7, 0.5, 0.6, 0.4],
        }}
        transition={{ 
          duration: 2.5 + (1 - intensity) * 1.5, 
          repeat: Infinity, 
          ease: "easeInOut" 
        }}
      />

      {/* Floating particles — motion pattern varies by mood quadrant, with
          per-particle randomized angle, reach, speed and start for organic drift. */}
      {intensity > 0.2 && particles.map((particle) => {
        const pm = getParticleMotion(quadrant, particle.angle, intensity, particle.reachMult);
        return (
          <motion.div
            key={particle.id}
            className="mood-particle"
            style={{ backgroundColor: adjustedColor }}
            initial={{
              opacity: 0,
              x: Math.cos(particle.angle * Math.PI / 180) * 45,
              y: Math.sin(particle.angle * Math.PI / 180) * 45,
            }}
            animate={pm.anim}
            transition={{
              duration: baseConfig.particleSpeed * 3 * particle.durMult,
              repeat: Infinity,
              delay: particle.delay,
              ease: pm.ease,
            }}
          />
        );
      })}
      
      {/* Time indicator removed */}
    </div>
  );
};

// Export for use in other components
export { detectQuadrant as detectEmotion };
